/**
 * Analytics Platform Integration (PostHog)
 * 
 * Implements Requirements 19.1, 19.2, 19.3, 19.7:
 * - Analytics platform integration
 * - Page view and navigation tracking
 * - User interaction tracking
 * - Privacy opt-out mechanisms
 */

import posthog from 'posthog-js';

// Privacy opt-out key
const OPT_OUT_KEY = 'analytics-opt-out';

/**
 * Check if user has opted out of analytics
 */
export function hasOptedOut(): boolean {
  return localStorage.getItem(OPT_OUT_KEY) === 'true';
}

/**
 * Opt out of analytics tracking
 */
export function optOut(): void {
  localStorage.setItem(OPT_OUT_KEY, 'true');
  if (posthog.has_opted_out_capturing()) {
    return;
  }
  posthog.opt_out_capturing();
  console.log('[Analytics] User opted out of tracking');
}

/**
 * Opt in to analytics tracking
 */
export function optIn(): void {
  localStorage.removeItem(OPT_OUT_KEY);
  if (!posthog.has_opted_out_capturing()) {
    return;
  }
  posthog.opt_in_capturing();
  console.log('[Analytics] User opted in to tracking');
}

/**
 * Initialize PostHog analytics
 * Should be called after React app initialization
 */
export function initAnalytics(): void {
  const apiKey = import.meta.env.VITE_POSTHOG_KEY;
  const apiHost = import.meta.env.VITE_POSTHOG_HOST || 'https://app.posthog.com';
  const enabled = import.meta.env.VITE_ANALYTICS_ENABLED === 'true';
  
  if (!apiKey) {
    console.warn('[Analytics] PostHog API key not configured. Analytics disabled.');
    return;
  }
  
  if (!enabled) {
    console.log('[Analytics] Analytics disabled via environment variable.');
    return;
  }
  
  // Check if user has opted out
  if (hasOptedOut()) {
    console.log('[Analytics] User has opted out. Analytics disabled.');
    return;
  }
  
  posthog.init(apiKey, {
    api_host: apiHost,
    
    // Privacy settings
    opt_out_capturing_by_default: false,
    respect_dnt: true, // Respect Do Not Track browser setting
    
    // Capture settings
    capture_pageview: true, // Automatically capture page views
    capture_pageleave: true, // Capture when user leaves page
    
    // Session recording (disabled by default for privacy)
    disable_session_recording: true,
    
    // Autocapture settings
    autocapture: {
      dom_event_allowlist: ['click', 'submit'], // Only capture clicks and form submissions
      url_allowlist: [], // Capture on all URLs
      element_allowlist: ['button', 'a', 'form'] as any[], // Type assertion for PostHog compatibility
    },
    
    // Persistence
    persistence: 'localStorage',
    
    // Advanced settings
    loaded: (posthog) => {
      if (import.meta.env.DEV) {
        console.log('[Analytics] PostHog initialized');
      }
    },
  });
}

/**
 * Track page view
 * Requirement 19.2
 */
export function trackPageView(path: string, properties?: Record<string, unknown>): void {
  if (hasOptedOut()) return;
  
  posthog.capture('$pageview', {
    $current_url: window.location.href,
    path,
    ...properties,
  });
}

/**
 * Track navigation event
 * Requirement 19.2
 */
export function trackNavigation(from: string, to: string, properties?: Record<string, unknown>): void {
  if (hasOptedOut()) return;
  
  posthog.capture('navigation', {
    from,
    to,
    ...properties,
  });
}

/**
 * Track user interaction (button click, form submission, etc.)
 * Requirement 19.3
 */
export function trackInteraction(
  action: string,
  category: string,
  label?: string,
  properties?: Record<string, unknown>
): void {
  if (hasOptedOut()) return;
  
  posthog.capture('interaction', {
    action,
    category,
    label,
    ...properties,
  });
}

/**
 * Track button click
 */
export function trackButtonClick(buttonName: string, properties?: Record<string, unknown>): void {
  trackInteraction('click', 'button', buttonName, properties);
}

/**
 * Track form submission
 */
export function trackFormSubmit(formName: string, properties?: Record<string, unknown>): void {
  trackInteraction('submit', 'form', formName, properties);
}

/**
 * Track search query
 */
export function trackSearch(query: string, results: number, properties?: Record<string, unknown>): void {
  if (hasOptedOut()) return;
  
  posthog.capture('search', {
    query,
    results,
    ...properties,
  });
}

/**
 * Track custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, unknown>): void {
  if (hasOptedOut()) return;
  
  posthog.capture(eventName, properties);
}

/**
 * Identify user for analytics
 * Call this after user authentication
 */
export function identifyUser(userId: string, properties?: {
  email?: string;
  name?: string;
  role?: string;
  [key: string]: unknown;
}): void {
  if (hasOptedOut()) return;
  
  // Anonymize sensitive data
  const sanitizedProperties = properties ? {
    ...properties,
    // Remove or hash sensitive fields
    email: properties.email ? hashEmail(properties.email) : undefined,
  } : undefined;
  
  posthog.identify(userId, sanitizedProperties);
}

/**
 * Reset user identity on logout
 */
export function resetUser(): void {
  posthog.reset();
}

/**
 * Set user properties
 */
export function setUserProperties(properties: Record<string, unknown>): void {
  if (hasOptedOut()) return;
  
  posthog.people.set(properties);
}

/**
 * Hash email for privacy (simple hash for analytics)
 */
function hashEmail(email: string): string {
  // Simple hash - in production, use a proper hashing algorithm
  let hash = 0;
  for (let i = 0; i < email.length; i++) {
    const char = email.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return `user_${Math.abs(hash)}`;
}

/**
 * Create analytics context provider hook
 */
export function useAnalytics() {
  return {
    trackPageView,
    trackNavigation,
    trackInteraction,
    trackButtonClick,
    trackFormSubmit,
    trackSearch,
    trackEvent,
    identifyUser,
    resetUser,
    setUserProperties,
    optOut,
    optIn,
    hasOptedOut: hasOptedOut(),
  };
}
